/**
 * Framework-free edit-lock network calls, plus the one piece of React Query
 * plumbing (`commitEditLockSnapshot`) that every writer of the lock cache
 * needs regardless of whether it runs inside a hook or a plain callback.
 *
 * Shared by the `useEditLockMutations` hook (which drives the session UI for
 * a mounted plan) and one-off call sites that cannot wait for a hook to mount
 * with a not-yet-known `planId` — e.g. acquiring the lock immediately after a
 * plan is created, before its detail page even exists.
 *
 * Contention is not an HTTP error here. Acquire and extend always answer `200`
 * and report the outcome in `acquired`, so the boolean in the body is the only
 * signal that decides whether this user may edit — never the status code, and
 * never our own reading of `editing_user`.
 */
import type { QueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { tpoGenUrls } from 'config/urls';

import { tpoGenKeys } from '../../utils/tpoGenQueryKeys';
import { toEditLockSnapshot } from './editLockAdapter';
import { readCurrentUserEmail, writeLastAcquiredPlan } from './editLockStorage';
import type { EditLockActionPayload, EditLockReleaseResponse, EditLockResponse, EditLockSnapshot } from './types';

export type LockEndpoint = (planId: string) => string;

export interface LockActionResult {
    snapshot: EditLockSnapshot;
    /** The server's verdict. Never inferred from the echoed holder. */
    acquired: boolean;
}

/**
 * The lock is keyed on this address server-side, so a missing one cannot be
 * papered over: it would acquire a lock nobody can release.
 */
export const requireUserId = (): string => {
    const userEmail = readCurrentUserEmail();
    if (!userEmail) throw new Error('No signed-in user to key the edit lock on.');
    return userEmail;
};

const lockRequestConfig = (userId: string) => ({
    method: 'POST' as const,
    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
    data: { user_id: userId } satisfies EditLockActionPayload,
});

export const postLockAction = async (url: LockEndpoint, planId: string): Promise<LockActionResult> => {
    const userId = requireUserId();
    const response = await axios<EditLockResponse>(url(planId), lockRequestConfig(userId));
    return {
        snapshot: toEditLockSnapshot(response.data, Date.now(), userId),
        acquired: response.data?.acquired === true,
    };
};

/** Resolves the server's `released` verdict; false means someone else holds it. */
export const postRelease = async (planId: string): Promise<boolean> => {
    const userId = requireUserId();
    const response = await axios<EditLockReleaseResponse>(
        tpoGenUrls.releasePlanEditLock(planId),
        lockRequestConfig(userId),
    );
    return response.data?.released === true;
};

/**
 * Acquires the lock outside any component lifecycle and records the "last
 * acquired plan" hint on success, mirroring what `EditSessionProvider.acquire`
 * does after a manual "Edit plan" confirm. Used right after plan creation,
 * where the planId only exists once the create call has already resolved —
 * too late to call a hook with it.
 */
export async function acquireEditLock(planId: string, planName: string): Promise<LockActionResult> {
    const result = await postLockAction(tpoGenUrls.acquirePlanEditLock, planId);
    if (result.acquired) writeLastAcquiredPlan(planId, planName);
    return result;
}

/**
 * Writes a freshly-known snapshot into the shared query cache, but cancels
 * any in-flight `edit/meta` poll for the same plan first.
 *
 * Without this, a poll that was already in flight when this write happens can
 * still resolve afterwards with the state it read *before* this write — e.g.
 * "free" from just before an acquire committed — and silently clobber this
 * more current result purely because its response landed later. Cancelling
 * discards that outdated response instead of letting it win the cache.
 */
export async function commitEditLockSnapshot(
    queryClient: QueryClient,
    planId: string,
    snapshot: EditLockSnapshot,
): Promise<void> {
    const queryKey = tpoGenKeys.editLock(planId);
    await queryClient.cancelQueries({ queryKey, exact: true });
    queryClient.setQueryData(queryKey, snapshot);
}
